import {
    gameLimits,
    getCurrentGame,
    getUniqueObjects,
    limitValueBetweenRange,
    objectInArray
} from "./helpers.js";
import {Superstars, Companies, Factions, CompanyLogs, db} from "../models/models.js";
import {getGameCurrentUserCompany} from "./games.js";
import {getCompanyLatestEvent, getCompanyRosterByType, getCompanyUnreadMessages} from "./companies.js";
import {addCompanyCompanyLogs, getCompanyChampionships} from "../models/company.js";
import {addRosterFactions, getRosterFactions, getRosterSuperstars, getRosterTagTeams} from "../models/roster.js";
import {
    getFactionSuperstars,
    getFactionLeaderSuperstar,
    removeFactionSuperstars,
    removeFactionLeaderSuperstar, getFactionFeuds, addFactionSuperstars, setFactionLeaderSuperstar
} from "../models/faction.js";
import {getTagTeamPopularity} from "./tagTeams.js";
import {compareChampionships} from "./championships.js";
import {getChampionshipsDisplayName} from "../ui/ui-helpers.js";
import {
    checkAvailability,
    checkSuperstarBrandAvailability,
    getGraphicForSuperstar, getSuperstarActiveFactions,
    getSuperstarChampionshipsFromCompanyChampionships
} from "./superstars.js";
import {getSuperstarLeadFactions} from "../models/superstar.js";
import {endFeud} from "./feuds.js";
import {CompanyLog} from "../models/companyLog.js";
import {checkTalentRequestAction} from "./talentRequests.js";

const factionReputationXps = [0, 20, 50, 90, 140, 200, 270, 350, 440, 540];


// BASIC FACTION FUNCTIONS

async function createFaction() {  // create_faction
    return
}

function setupCreateFaction(newFunction) {
    createFaction = newFunction
}

async function addSuperstarsToFaction(faction, superstars) {  // add_faction_superstars
    const factionSuperstars = await getFactionSuperstars(faction);
    for (const superstar of superstars) {
        if (!objectInArray(superstar, factionSuperstars)) {
            await addFactionSuperstars(faction, [superstar])
        }
    }
}

async function removeSuperstarsFromFaction(faction, superstars) {  // remove_faction_superstars
    const leader = await getFactionLeaderSuperstar(faction);
    const factionSuperstars = await getFactionSuperstars(faction);
    for (const superstar of superstars) {
        if (objectInArray(superstar, factionSuperstars)) {
            await removeFactionSuperstars(faction, [superstar]);

            // Add to faction's former members array if not present already
            if (faction.formerMembers.indexOf(superstar.name) < 0) {
                faction.formerMembers.push(superstar.name)
            }

            // Remove leader status if removing from faction
            if (leader && superstar.$loki === leader.$loki) {
                await removeFactionLeader(faction, leader)
            }
        }
    }
    return faction;
}

async function removeFactionLeader(faction, leader) {  // remove_faction_leader
    if (leader) {
        const leadsFactions = await getSuperstarLeadFactions(leader);
        if (objectInArray(faction, leadsFactions)) {
            await removeFactionLeaderSuperstar(faction)
        }
    }
    return faction;
}

async function disbandFaction(faction, month, year) {  // disband_faction
    faction.disbanded = true;
    // End feuds
    for (const feud of (await getFactionFeuds(faction))) {
        if (!feud.ended) {
             await endFeud(feud, month, year);
        }
    }
}

// END BASIC FACTION FUNCTIONS

async function getViewFactionsData(companyUid) {  // get_company_factions_view
    const currentGame = await getCurrentGame();

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = await getGameCurrentUserCompany(currentGame)
    }

    // Get main roster's factions and sort
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const mainRosterFactions = await getRosterFactions(mainRoster);
    mainRosterFactions.sort((a, b) => a.disbanded - b.disbanded || b.reputation - a.reputation ||
        b.xp - a.xp || b.$loki - a.$loki);

    // Get company championships
    const companyChampionships = await getCompanyChampionships(selectedCompany);

    // Get data for factions
    return Promise.all(mainRosterFactions.map(x => getFactionData(x, companyChampionships)));
}

async function getFactionData(faction, companyChampionships, getSuperstarsInformation = false) {  // get_faction_data
    let data = {};

    // Get attributes
    data["name"] = faction.name;
    data["disbanded"] = faction.disbanded;
    data["uid"] = faction.$loki;
    data["type"] = 'faction';

    // Get superstars data
    const factionSuperstars = await getFactionSuperstars(faction);
    data["superstars"] = await getFactionSuperstarsData(faction, factionSuperstars, companyChampionships);

    // Get graphics
    data["graphic"] = await getFactionGraphicsData(faction, factionSuperstars, companyChampionships);

    // Get former members, removing those who have rejoined faction
    const factionSuperstarsNames = factionSuperstars.map(x => x.name);
    data["formerMembers"] = faction.formerMembers.filter(x => factionSuperstarsNames.indexOf(x) < 0);

    // Get reputation data
    data["reputationData"] = getFactionReputationData(faction);

    // Get data for superstars/booking
    if (getSuperstarsInformation && factionSuperstars.length) {
        data["popularity"] = await getTagTeamPopularity(faction, factionSuperstars);
        data["pronoun"] = (factionSuperstars.every(x => x.pronoun === factionSuperstars[0].pronoun)) ? factionSuperstars[0].pronoun : null;
        data["alignment"] = (factionSuperstars.every(x => x.alignment === factionSuperstars[0].alignment)) ? factionSuperstars[0].alignment : null;
        data["brandName"] = (factionSuperstars.every(x => x.brandName === factionSuperstars[0].brandName)) ? factionSuperstars[0].brandName : null;
        data["unhappy"] = factionSuperstars.some(x => x.unhappy);
        data["partTimer"] = factionSuperstars.every(x => x.partTimer);
        data["charisma"] = factionSuperstars.reduce((n, {charisma}) => n + charisma, 0) / factionSuperstars.length;
        data["workrate"] = factionSuperstars.reduce((n, {workrate}) => n + workrate, 0) / factionSuperstars.length;
        data["overness"] = factionSuperstars.reduce((n, {overness}) => n + overness, 0) / factionSuperstars.length;
        data["age"] = factionSuperstars.reduce((n, {age}) => n + age, 0) / factionSuperstars.length;
        data["morale"] = factionSuperstars.reduce((n, {morale}) => n + morale, 0) / factionSuperstars.length;
        data["fatigue"] = factionSuperstars.reduce((n, {fatigue}) => n + fatigue, 0) / factionSuperstars.length;
    }

    return data;
}

async function getFactionSuperstarsData(faction, factionSuperstars, companyChampionships) {  // get_faction_superstars_data
    let data = [];

    const factionLeaderSuperstar = await getFactionLeaderSuperstar(faction);
    for (const superstar of factionSuperstars) {

        // Get superstar championships
        const superstarChampionships = companyChampionships.filter(x => x.superstarId === superstar.$loki);
        superstarChampionships.sort(compareChampionships);

        // Get data for superstar
        let d = {};
        d["name"] = superstar.name;
        d["championshipsNames"] = superstarChampionships.length > 0 ?
            getChampionshipsDisplayName(superstarChampionships, true, false, false) : null;
        d["leader"] = factionLeaderSuperstar ? superstar.$loki === factionLeaderSuperstar.$loki : false;
        d["fullTimeManager"] = superstar.fullTimeManager;
        data.push(d);
    }

    // Sort by leader first
    data.sort((a, b) => b["leader"] - a["leader"]);

    return data;
}

async function getFactionGraphicsData(faction, factionSuperstars, companyChampionships) {  // get_faction_graphics
    let graphics = [];

    const factionLeaderSuperstar = await getFactionLeaderSuperstar(faction);
    let factionLeaderSuperstarGraphic;
    for (const superstar of factionSuperstars) {
        // Get superstar's graphic
        let superstarGraphic = getGraphicForSuperstar(superstar);

        // Add title graphic if superstar is champion
        const superstarChampionship = companyChampionships.find(x => x.superstarId === superstar.$loki);
        if (superstarChampionship && superstarGraphic) {
            superstarGraphic["title"] = JSON.parse(JSON.stringify(superstarChampionship.graphic))
        }

        graphics.push(superstarGraphic);

        // Set faction leader graphic if superstar is leader
        if (factionLeaderSuperstar && superstar.$loki === factionLeaderSuperstar.$loki) {
            factionLeaderSuperstarGraphic = superstarGraphic
        }
    }

    // Move leader graphic to center position
    if (factionLeaderSuperstarGraphic) {
        const factionLeaderSuperstarIndex = graphics.indexOf(factionLeaderSuperstarGraphic);
        const centerIndex = Math.floor((graphics.length + 1) / 2) - 1;
        graphics.splice(factionLeaderSuperstarIndex, 1);
        graphics.splice(centerIndex, 0, factionLeaderSuperstarGraphic);
    }

    return graphics;
}

function getFactionReputationData(faction) {  // get_faction_reputation_data
    let data = {};

    data["reputation"] = faction.reputation;
    data["xp"] = faction.xp;

    if (faction.reputation < 10) {
        const xpToNextReputation = factionReputationXps[faction.reputation];
        const xpToPreviousReputation = factionReputationXps[faction.reputation - 1];
        data["xpToNextReputation"] = xpToNextReputation;
        data["progressPercentage"] = (faction.xp - xpToPreviousReputation) / (xpToNextReputation - xpToPreviousReputation) * 100
    }
    else {
        data["xpToNextReputation"] = factionReputationXps[faction.reputation - 1];
        data["progressPercentage"] = 100;
    }

    return data;
}

async function getRosterActiveFactions(roster) {
    const factions = (await getRosterFactions(roster)).filter(x => !x.disbanded);
    factions.sort((a, b) => b.reputation - a.reputation || b.xp - a.xp);
    return factions;
}

async function getSuperstarsAvailableForFaction({factionUid, checkBrandExclusivity, excludeFactionSuperstars} = {}) {  // get_superstars_available_for_faction_view
    let data = {};
    const currentGame = await getCurrentGame();
    let selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const currentEvent = await getCompanyLatestEvent(selectedCompany);
    const companyChampionships = await getCompanyChampionships(selectedCompany);

    // Get faction
    let faction;
    if (factionUid) {
        faction = await Factions.get(factionUid)
    }

    // Get faction superstars
    let factionSuperstars = faction && !excludeFactionSuperstars ? await getFactionSuperstars(faction) : [];

    // Check for brand exclusive event
    let availableSuperstars = await getRosterSuperstars(mainRoster);
    if (checkBrandExclusivity) {
        availableSuperstars = availableSuperstars.filter(x => checkSuperstarBrandAvailability(currentEvent, x,
            getSuperstarChampionshipsFromCompanyChampionships(x, companyChampionships)));
    }

    // Get data for available superstars
    data['availableSuperstars'] = availableSuperstars.filter(x => !objectInArray(x, factionSuperstars)).map(x => {
        return {'name': x.name, 'uid': x.$loki}
    });
    data['availableSuperstars'].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    return data;
}

async function getFactionSuperstarsRequest({factionUid} = {}) {  // get_faction_superstars_view
    let data = {};

    // Get faction
    let faction;
    if (factionUid) {
        faction = await Factions.get(factionUid)
    }
    if (!faction) {
        data['error'] = 'faction-not-found';
        return data;
    }

    // Get data for faction superstars
    data['factionSuperstars'] = (await getFactionSuperstars(faction)).map(x => {
        return {'name': x.name, 'uid': x.$loki}
    });

    return data;
}

async function addFaction(name, superstarUids, leaderSuperstarUid) {  // add_faction_view
    let data = {};
    const currentGame = await getCurrentGame();
    let selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const rosterTagTeams = await getRosterTagTeams(mainRoster);
    const rosterActiveFactions = await getRosterActiveFactions(mainRoster);
    const cost = 20;

    // Check if enough points
    if (selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Check if faction already exists
    if (rosterActiveFactions.find(x => x.name === name)) {
        data['error'] = 'already-present';
        return data;
    }

    // Check if team already exists
    if (rosterTagTeams.find(x => x.name === name)) {
        data['error'] = 'team-already-present';
        return data;
    }

    // Get superstars
    let factionSuperstars = [];
    for (const superstarUid of superstarUids) {
        let superstar = rosterSuperstars.find(x => x.$loki === superstarUid);

        // Check if superstar present and not in a faction
        if (!superstar) {
            data['error'] = 'superstar-not-found';
            return data;
        }
        const superstarActiveFactions = await getSuperstarActiveFactions(superstar);
        if (superstarActiveFactions.length > 0) {
            data['error'] = 'already-in-faction';
            data['errorSuperstarName'] = superstar.name;
            return data;
        }
        factionSuperstars.push(superstar);
    }

    // Remove duplicates
    factionSuperstars = getUniqueObjects(factionSuperstars);

    // Get leader superstar
    let leaderSuperstar;
    if (leaderSuperstarUid) {
        leaderSuperstar = rosterSuperstars.find(x => x.$loki === leaderSuperstarUid);
        if (leaderSuperstar && !objectInArray(leaderSuperstar, factionSuperstars)) {
            data['error'] = 'leader-superstar-not-present';
            return data;
        }
    }

    // Check if number of superstars are between 2 and maximum
    if (factionSuperstars.length < 2) {
        data['error'] = 'minimum-superstars';
        return data;
    }
    if (factionSuperstars.length > gameLimits.maxFactionSuperstars) {
        data['error'] = 'maximum-superstars';
        return data;
    }

    // Create faction and add to roster
    let reputationXpData = getFactionCreationReputationAndXp(factionSuperstars, leaderSuperstar);
    const newFaction = await createFaction(name, factionSuperstars, reputationXpData.reputation,
        reputationXpData.xp, leaderSuperstar);
    await addRosterFactions(mainRoster, [newFaction]);

    // Add company log
    const newCompanyLog = CompanyLog(currentGame.monthCount, selectedCompany.realYear);
    await CompanyLogs.insert(newCompanyLog);
    newCompanyLog.text = newFaction.name;
    newCompanyLog.type = 'new-faction';
    newCompanyLog.realYear = currentGame.year;
    await addCompanyCompanyLogs(selectedCompany, [newCompanyLog]);

    // Check start faction request
    for (const factionSuperstar of factionSuperstars) {
        await checkTalentRequestAction({company: selectedCompany, requestType: 'start_faction', talent: factionSuperstar});
    }

    // Remove points
    selectedCompany.points -= cost;

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['factionName'] = newFaction.name;
    const unreadMessages = await getCompanyUnreadMessages(selectedCompany);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

function getFactionCreationReputationAndXp(factionSuperstars, leaderSuperstar = null) {  // get_faction_creation_reputation_and_xp

    // Add double weight for leader superstar
    factionSuperstars = [...factionSuperstars];
    if (leaderSuperstar) {
        factionSuperstars.push(leaderSuperstar)
    }

    // Get xp using popularity of faction superstars and hot/stale
    let totalXp = 0;
    for (const s of factionSuperstars) {
        const popularity = s.popularity >= 0 ? s.popularity : 0;
        let xp = Math.pow(Math.floor(popularity / 110), 2.45);
        if (s.hot) {
            xp += 30
        }
        if (s.stale) {
            xp -= 30
        }
        xp = limitValueBetweenRange(xp, 0, 220);
        totalXp += xp;
    }
    let factionXp = Math.floor(totalXp / factionSuperstars.length);

    // Get reputation from xp
    let reputation = 1;
    for (const [index, factionReputationXp] of factionReputationXps.entries()) {
        if (factionXp >= factionReputationXp) {
            reputation = index + 1
        }
    }

    return {reputation: reputation, xp: factionXp}
}

function updateFactionXpAndReputation(faction, xpChange) {  // update_faction_xp_and_reputation
    // Update xp
    faction.xp += xpChange;
    faction.xpGainedThisYear += xpChange;
    if (faction.xp < 0) {
        faction.xp = 0
    }

    // Update reputation
    for (const [index, factionReputationXp] of factionReputationXps.entries()) {
        if (faction.xp >= factionReputationXp) {
            faction.reputation = index + 1
        }
    }

    // Get reputation data
    let data = getFactionReputationData(faction);
    data['xpChange'] = xpChange;
    data['factionName'] = faction.name;
    data['uid'] = faction.$loki;

    return data;
}

async function disbandFactionRequest(factionUid) {  // disband_faction_view
    let data = {};
    const currentGame = await getCurrentGame();

    // Get faction
    let faction =  await Factions.get(factionUid);
    if (!faction) {
        data['error'] = 'faction-not-found';
        return data;
    }

    // Disband faction
    await disbandFaction(faction, currentGame.monthCount, currentGame.year);

    db.saveDatabase();

    // Get data to return
    data['factionName'] = faction.name;

    return data;
}

async function addFactionMembers(factionUid, superstarUids) {  // add_faction_superstars_view
    let data = {};
    const currentGame = await getCurrentGame();
    let selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);

    // Get faction
    let faction =  await Factions.get(factionUid);
    if (!faction) {
        data['error'] = 'faction-not-found';
        return data;
    }

    // Get superstars to add
    const existingFactionSuperstars = await getFactionSuperstars(faction);
    let superstarsToAdd = [];
    for (const superstarUid of superstarUids) {
        let superstar = rosterSuperstars.find(x => x.$loki === superstarUid);

        // Check if superstar present and not in this or other faction
        if (!superstar) {
            data['error'] = 'superstar-not-found';
            return data;
        }
        if (objectInArray(superstar, existingFactionSuperstars)) {
            data['error'] = 'already-member-of-faction';
            data['errorSuperstarName'] = superstar.name;
            return data;
        }
        const superstarActiveFactions = await getSuperstarActiveFactions(superstar);
        if (superstarActiveFactions.length > 0) {
            data['error'] = 'already-in-faction';
            data['errorSuperstarName'] = superstar.name;
            return data;
        }
        superstarsToAdd.push(superstar);
    }

    // Remove duplicates
    superstarsToAdd = getUniqueObjects(superstarsToAdd);

    // Check if faction superstars are below maximum
    if (existingFactionSuperstars.length + superstarsToAdd.length > gameLimits.maxFactionSuperstars) {
        data['error'] = 'maximum-superstars';
        return data;
    }

    // Lower faction reputation xp
    const xpChange = -20;
    updateFactionXpAndReputation(faction, xpChange);

    // Add faction superstars
    await addSuperstarsToFaction(faction, superstarsToAdd);

    // Check join faction request
    for (const superstarToAdd of superstarsToAdd) {
        let requestData = {'factionId': faction.$loki};
        await checkTalentRequestAction({company: selectedCompany, requestType: 'join_faction', talent: superstarToAdd,
            data: requestData});
    }

    db.saveDatabase();

    // Get data to return
    data['factionName'] = faction.name;
    data['xpChange'] = xpChange;
    data['addedSuperstarsNames'] = superstarsToAdd.map(x => x.name);
    const unreadMessages = await getCompanyUnreadMessages(selectedCompany);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function removeFactionMembers(factionUid, superstarUids) {  // remove_faction_superstars_view
    let data = {};
    const currentGame = await getCurrentGame();
    let selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);

    // Get faction
    let faction =  await Factions.get(factionUid);
    if (!faction) {
        data['error'] = 'faction-not-found';
        return data;
    }

    // Get superstars to remove
    const existingFactionSuperstars = await getFactionSuperstars(faction);
    let superstarsToRemove = [];
    for (const superstarUid of superstarUids) {
        let superstar = rosterSuperstars.find(x => x.$loki === superstarUid);

        // Check if superstar present and in this faction
        if (!superstar) {
            data['error'] = 'superstar-not-found';
            return data;
        }
        if (!objectInArray(superstar, existingFactionSuperstars)) {
            data['error'] = 'not-member-of-faction';
            data['errorSuperstarName'] = superstar.name;
            return data;
        }
        superstarsToRemove.push(superstar);
    }

    // Remove duplicates
    superstarsToRemove = getUniqueObjects(superstarsToRemove);

    // Check if faction superstars are above 2
    if (existingFactionSuperstars.length - superstarsToRemove.length < 2) {
        data['error'] = 'minimum-superstars';
        return data;
    }

    // Lower faction reputation xp
    const xpChange = -20;
    updateFactionXpAndReputation(faction, xpChange);

    // Remove faction superstars
    await removeSuperstarsFromFaction(faction, superstarsToRemove);

    // Check leave faction request
    for (const superstarToRemove of superstarsToRemove) {
        let requestData = {'factionId': faction.$loki};
        await checkTalentRequestAction({company: selectedCompany, requestType: 'leave_faction', talent: superstarToRemove,
            data: requestData});
    }

    db.saveDatabase();

    // Get data to return
    data['factionName'] = faction.name;
    data['xpChange'] = xpChange;
    data['removedSuperstarsNames'] = superstarsToRemove.map(x => x.name);
    const unreadMessages = await getCompanyUnreadMessages(selectedCompany);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function removeFactionLeaderRequest(factionUid) {  // remove_faction_leader_view
    let data = {};

    // Get faction
    let faction =  await Factions.get(factionUid);
    if (!faction) {
        data['error'] = 'faction-not-found';
        return data;
    }

    // Check if faction leader present
    let leaderSuperstar = await getFactionLeaderSuperstar(faction);
    if (!leaderSuperstar) {
        data['error'] = 'leader-not-present';
        return data;
    }

    // Lower faction reputation xp
    const xpChange = -10;
    updateFactionXpAndReputation(faction, xpChange);

    // Remove faction leader
    await removeFactionLeader(faction, leaderSuperstar);

    db.saveDatabase();

    // Get data to return
    data['factionName'] = faction.name;
    data['xpChange'] = xpChange;
    data['removedLeaderSuperstarName'] = leaderSuperstar.name;

    return data;
}

async function setFactionLeader(factionUid, leaderSuperstarUid) {  // set_faction_leader_view
    let data = {};

    // Get faction
    let faction =  await Factions.get(factionUid);
    if (!faction) {
        data['error'] = 'faction-not-found';
        return data;
    }

    // Get leader superstar
    let leaderSuperstar = await Superstars.get(leaderSuperstarUid);
    if (!leaderSuperstar) {
        data['error'] = 'leader-superstar-not-found';
        return data;
    }
    const factionSuperstars = await getFactionSuperstars(faction);
    if (!objectInArray(leaderSuperstar, factionSuperstars)) {
        data['error'] = 'not-member-of-faction';
        data['errorSuperstarName'] = leaderSuperstar.name;
        return data;
    }

    // Lower faction reputation xp
    const xpChange = -10;
    updateFactionXpAndReputation(faction, xpChange);

    // Set faction leader
    await setFactionLeaderSuperstar(faction, leaderSuperstar);

    db.saveDatabase();

    // Get data to return
    data['factionName'] = faction.name;
    data['xpChange'] = xpChange;
    data['leaderSuperstarName'] = leaderSuperstar.name;

    return data;
}

async function getCompanyFactionsForSelection() {  // get_company_factions_for_selection_view
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const factions = await getRosterActiveFactions(mainRoster);
    data['factions'] = factions.map(x => {
        return {'name': x.name, 'uid': x.$loki}
    });
    return data;
}

async function getFactionSuperstarsForSegment(faction, event, companyChampionships) {  // get_faction_superstars_for_segment
    let factionSuperstars = await getFactionSuperstars(faction);
    return factionSuperstars.filter(x => checkAvailability(x, true, true) &&
        checkSuperstarBrandAvailability(event, x, getSuperstarChampionshipsFromCompanyChampionships(x, companyChampionships)));
}

async function processFactionXpChangeForSuperstar({superstar, championshipWon = null, championshipLost = null,
                                                  overnessChange = null, fotcChange = null} = {}) {  // process_faction_xp_change_for_superstar
    let factionReputationsData = [];

    // Process faction xp change for superstar's active factions
    const superstarFactions = await getSuperstarActiveFactions(superstar);
    for (const faction of superstarFactions) {
        const factionReputationData = await processFactionXpChangeForFaction({faction, superstar, championshipWon,
            championshipLost, overnessChange, fotcChange});
        factionReputationsData.push(factionReputationData);
    }

    return factionReputationsData;
}

async function processFactionXpChangeForFaction({faction, superstar = null, championshipWon = null, championshipLost = null,
                                                 overnessChange = null, fotcChange = null, addedMembers = null,
                                                 removedMembers = null, feudWon = null, feudLost = null} = {}) {  // process_faction_xp_change_for_faction
    const xpChangeData = await getFactionXpChange(faction, superstar, championshipWon, championshipLost,
        overnessChange, fotcChange, addedMembers, removedMembers, feudWon, feudLost);
    const xpChange = xpChangeData['xpChange'];
    const data = updateFactionXpAndReputation(faction, xpChange);
    data['xpChangeDetails'] = xpChangeData;

    return data;
}

async function getFactionXpChange(faction, superstar = null, championshipWon = null, championshipLost = null,
                                  overnessChange = null, fotcChange = null, addedMembers = null, removedMembers = null,
                                  feudWon = null, feudLost = null) {  // get_faction_xp_change
    const data = {};
    const leaderSuperstar = await getFactionLeaderSuperstar(faction);

    // Check if leader present
    let isLeader = (superstar && leaderSuperstar && superstar.$loki === leaderSuperstar.$loki);
    if (addedMembers && leaderSuperstar && objectInArray(leaderSuperstar, addedMembers)) {
        isLeader = true;
    }
    if (removedMembers && leaderSuperstar && objectInArray(leaderSuperstar, removedMembers)) {
        isLeader = true;
    }

    // Get xp change based on action
    if (championshipWon || championshipLost) {
        const championshipChangeData = getFactionXpChangeForChampionships(championshipWon, championshipLost, isLeader);
        if (championshipWon) {
            data['championshipWon'] = championshipChangeData;
        } else if (championshipLost) {
            data['championshipLost'] = championshipChangeData;
        }
    }

    if (overnessChange) {
        const overnessChangeData = getFactionXpChangeForSuperstarOverness(overnessChange, isLeader);
        if (overnessChange === 'hot') {
            data['hot'] = overnessChangeData;
        } else if (overnessChange === 'stale') {
            data['stale'] = overnessChangeData;
        }
    }

    if (fotcChange) {
        const fotcChangeData = getFactionXpChangeForFotcChange(fotcChange, isLeader);
        if (fotcChange === 'becameFotc') {
            data['becameFotc'] = fotcChangeData;
        }
        if (fotcChange === 'lostFotc') {
            data['lostFotc'] = fotcChangeData;
        }
    }

    if (addedMembers || removedMembers) {
        const membersChangeData = getFactionXpChangeForMembersChange(addedMembers, removedMembers, leaderSuperstar);
        if (addedMembers) {
            data['addedMembers'] = membersChangeData;
        } else if (removedMembers) {
            data['removedMembers'] = membersChangeData;
        }
    }

    if (feudWon || feudLost) {
        const feudChangeData = getFactionXpChangeForFeud(feudWon, feudLost);
        if (feudWon) {
            data['feudWon'] = feudChangeData;
        } else if (feudLost) {
            data['feudLost'] = feudChangeData;
        }
    }

    // Get total xp change
    data['xpChange'] = Object.values(data).reduce((total, xpChange) => total + xpChange, 0);
    data['isLeader'] = isLeader;

    return data;
}

function getFactionXpChangeForChampionships(championshipWon, championshipLost, isLeader = false) {  // get_faction_xp_change_for_championships
    let xpChange = 0;
    const championshipWonPrestigeData = {'S': 40, 'A': 30, 'B': 18, 'C': 10};
    const championshipLostPrestigeData = {'S': 35, 'A': 25, 'B': 15, 'C': 8};

    if (championshipWon) {
        xpChange += championshipWonPrestigeData[championshipWon.prestige] || 10;
    } else if (championshipLost) {
        xpChange -= championshipLostPrestigeData[championshipLost.prestige] || 8;
    }

    if (isLeader) {
        xpChange = Math.floor(xpChange * 1.25);
    }

    return xpChange;
}

function getFactionXpChangeForSuperstarOverness(overnessChange, isLeader = false) {  // get_faction_xp_change_for_superstar_overness
    let xpChange = 0;

    if (overnessChange === 'hot') {
        xpChange = 20;
    }
    if (overnessChange === 'stale') {
        xpChange = -20;
    }

    if (isLeader) {
        xpChange = Math.floor(xpChange * 1.25);
    }

    return xpChange;
}

function getFactionXpChangeForFotcChange(fotcChange, isLeader = false) {
    let xpChange = 0;

    if (fotcChange === 'becameFotc') {
        xpChange = 30;
    }
    if (fotcChange === 'lostFotc') {
        xpChange = -30;
    }

    if (isLeader) {
        xpChange = Math.floor(xpChange * 1.25);
    }

    return xpChange;
}

function getFactionXpChangeForMembersChange(addedMembers, removedMembers, leaderSuperstar) {  // get_faction_xp_change_for_members_change
    let xpChange = 0;

    // Get list of added and removed members
    let allMembers = [];
    if (addedMembers) {
        allMembers.push(...addedMembers);
    }
    if (removedMembers) {
        allMembers.push(...removedMembers);
    }

    for (let s of allMembers) {
        let xp = Math.floor(Math.pow(s.popularity / 440, 5));
        if (s.hot) {
            xp += 20;
        }
        if (s.stale) {
            xp -= 20;
        }
        if (s.faceOfTheCompany) {
            xp += 30;
        }
        xp = limitValueBetweenRange(xp, -20, 100);

        if (leaderSuperstar && s.$loki === leaderSuperstar.$loki) {
            xp = Math.floor(xp * 1.25);
        }
        if (addedMembers && objectInArray(s, addedMembers)) {
            xpChange += xp;
        } else if (removedMembers && objectInArray(s, removedMembers)) {
            // Higher penalty for removed member
            if (xp > 0) {
                xp = Math.floor(xp * 1.25);
            }
            xpChange -= xp;
        }
    }

    return xpChange;
}

function getFactionXpChangeForFeud(feudWon = null, feudLost = null) {  // get_faction_xp_change_for_feud
    let xpChange = 0;

    if (feudWon) {
        xpChange += 15;
        let hypeBonus = Math.floor(feudWon.hype / 10);
        hypeBonus = limitValueBetweenRange(hypeBonus, -20, 20);
        xpChange += hypeBonus;
    } else if (feudLost) {
        xpChange -= 12;
        let hypeBonus = Math.floor(feudLost.hype / 12);
        hypeBonus = limitValueBetweenRange(hypeBonus, -16, 16);
        xpChange -= hypeBonus;
    }

    return xpChange;
}


export {getViewFactionsData, factionReputationXps, removeSuperstarsFromFaction, disbandFaction,
    getRosterActiveFactions, setFactionLeader, addFaction, addFactionMembers, getFactionData,
    removeFactionSuperstars, removeFactionLeaderSuperstar, removeFactionMembers, removeFactionLeaderRequest,
    disbandFactionRequest, getFactionSuperstarsRequest, getSuperstarsAvailableForFaction,
    getCompanyFactionsForSelection, getFactionSuperstarsForSegment, processFactionXpChangeForSuperstar,
    processFactionXpChangeForFaction, removeFactionLeader, getFactionCreationReputationAndXp, createFaction,
    setupCreateFaction, updateFactionXpAndReputation}